import { format, parseISO } from "date-fns"
import { useState, useMemo } from "react"
import IKanbanBoardProductionSchemaProps from "../../../Components/KanbanBoardProduction/interfaces/kanban-board-production-schema.interface"
import { Environment } from "../../../Config/constants"
import { ServiceOrderStatusEnum } from "../../../Services/api/service-orders/service-order.interface"
import { BillProductOrderOrdersUseCase } from "../../../Usecase/Orders/bill-products-order-usecase"
import { ListOrdersUseCase } from "../../../Usecase/Orders/list-orders-usecase"
import { useToast } from "../../../Hooks/ToasContext"
import { ShowOrdersUseCase } from "../../../Usecase/Orders/show-order-usecase"

import { ptBR } from 'date-fns/locale';
import { FormatMoney } from "../../../utils/formatMoney"
import IKanbanBoardFinancialSchemaProps from "../../../Components/KanbanBoardFinancial/interfaces/kanban-board-financial-schema.interface"
import { ShowOmieOrderDetailsUseCase } from "../../../Usecase/Orders/show-omie-order-details-usecase"
import { UpdateOrdersUseCase } from "../../../Usecase/Orders/update-order-usecase"
import { ISellerEntity } from "../../../Services/api/sellers/sellers.interface"
import { useNavigate } from "react-router-dom"
import { ICustomerEntity } from "../../../Services/api/customers/customers.interface"
import { IOrderEntity, IOrdersRegisteredOnOmieEntity, IBillProductOrderPayload, TBillingFlow } from "../../../Services/api/orders/orders.interface"
import { IAddressEntity } from "../../../Services/api/addresses/addresses.interface"
import { SubmitHandler, useForm } from "react-hook-form"
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'


interface IUseDetailsFinancialProps {
  listOrdersUseCase: ListOrdersUseCase
  billProductOrderOrdersUseCase: BillProductOrderOrdersUseCase
  showOrdersUseCase: ShowOrdersUseCase
  orderId: string;
  showOmieOrderDetailsUseCase: ShowOmieOrderDetailsUseCase
  updateOrdersUseCase: UpdateOrdersUseCase
}

interface IUpdateObservationPayload {
  observations: string
}

const UpdateObservationSchemaValidation = Yup.object().shape({
  observations: Yup.string(),
});

export interface Task {
  id: string;
  content: {
    os: string;
    items: any; // ajuste o tipo conforme sua necessidade
    type?: 'checkbox' | 'click' | 'navigate'
  };
}

export interface ISaleEntity {
  id: string;
  createdAt: string;
  customer: ICustomerEntity;
  total: string;
  shippingAddress: IAddressEntity;
  billingAddress: IAddressEntity;
  products: {
    id: string;
    name: string;
    quantity: number;
    variant: string;
    sku: string;
    price_unit: string;
    total: string;
    image: string;
  }[]
}

const tasks: Record<string, Task> = {};
export function useDetailsFinancial({ listOrdersUseCase, billProductOrderOrdersUseCase, showOrdersUseCase, orderId, showOmieOrderDetailsUseCase, updateOrdersUseCase }: IUseDetailsFinancialProps){
    const [loading, setLoading] = useState(true)
    const [orderData, setOrderData] = useState<IOrderEntity>()
    const [kanbanFinancialSchema, setKanbanFinancialSchema] = useState<IKanbanBoardFinancialSchemaProps>()
    const [kanbanProductionSchema, setKanbanProductionSchema] = useState<IKanbanBoardProductionSchemaProps>()
    const [productsToBill, setProductsToBill] = useState<string[]>([])
    const [omieOrderData,setOmieOrderData] = useState<IOrdersRegisteredOnOmieEntity | undefined>(undefined)
    const [showModalOrderWithoutSeller, setShowModalOrderWithoutSeller] = useState(false)
    const [sellerSelected, setSellerSelected] = useState<ISellerEntity>()
    const [showCustomersModal, setShowCustomersModal] = useState(false)
    const navigate = useNavigate()
    const { handleSubmit, control, setValue, register } = useForm<IUpdateObservationPayload>({
      resolver: yupResolver(UpdateObservationSchemaValidation),
  });
    const { showToast } =  useToast()

    const transformProductionData = (dados: IOrderEntity) => {
      
      const tasks: Record<string, Task> = {};
      const initialOS : any = [];
      const onProductionOS : any = [];
      const finishedOS : any = [];
      
      dados.products.forEach((item, index) => {
          // const taskId = `task-${index + 1}`;
          const taskId = item.id;
          tasks[taskId] = {
              id: taskId,
              content: {
                  os: `Produto - ${item.product.name}`,
                  items: item.product
              }
          };

          if(item.status === ServiceOrderStatusEnum.INITIAL){
            initialOS.push(taskId);
          } else if ([ServiceOrderStatusEnum.PROCESSING, ServiceOrderStatusEnum.ON_GOING, ServiceOrderStatusEnum.PRODUCTION].includes(item.status)){
            onProductionOS.push(taskId)
          } else {
            finishedOS.push(taskId)
          }
          
      });

      const result = {
          tasks,
          columns: {
            [ServiceOrderStatusEnum.INITIAL]: {
                  id: ServiceOrderStatusEnum.INITIAL,
                  title: 'Aguardando Início',
                  taskIds: initialOS,
              },
              [ServiceOrderStatusEnum.PROCESSING]: {
                  id: ServiceOrderStatusEnum.PROCESSING,
                  title: 'Em Produção',
                  taskIds: onProductionOS,
              },
              [ServiceOrderStatusEnum.PRODUCED]: {
                  id: ServiceOrderStatusEnum.PRODUCED,
                  title: 'Produzido',
                  taskIds: finishedOS,
              },
          },
          columnOrder: [ServiceOrderStatusEnum.INITIAL, ServiceOrderStatusEnum.PROCESSING, ServiceOrderStatusEnum.PRODUCED],
      };

      return result;
    }

    const transformFinancialData = (dados: IOrderEntity) => {
      
      const tasks: Record<string, Task> = {};
      const readyToBilled : any = [];
      const billedOrders : any = [];
      
      dados?.products?.forEach((item, index) => {
          // const taskId = `task-${index + 1}`;
          const taskId = item.id;
          tasks[taskId] = {
              id: taskId,
              content: {
                  os: `Produto ${item.product.name}`,
                  items: item.product,
                  type: 'checkbox'
              }
          };

          if(item.status === ServiceOrderStatusEnum.PRODUCED){
            readyToBilled.push(taskId);
          } 
          
      });

      dados?.ordersRegisteredOnOmie?.forEach((item, index) => {
        // const taskId = `task-${index + 1}`;
        const taskId = item.id
        tasks[taskId] = {
            id: taskId,
            content: {
                os: `Pedido OMIE: ${item?.omieOriginalOrderId}`,
                items: item.serviceOrders,
                type: 'navigate'
            }
        };

        billedOrders.push(taskId);
        
    });



      const result = {
          tasks,
          columns: {
            [ServiceOrderStatusEnum.PRODUCED]: {
                  id: ServiceOrderStatusEnum.PRODUCED,
                  title: 'Aguardando Faturamento',
                  taskIds: readyToBilled,
                  checkAll: true
              },
              [ServiceOrderStatusEnum.BILLED]: {
                  id: ServiceOrderStatusEnum.BILLED,
                  title: 'Faturado',
                  taskIds: billedOrders,
              },
             
          },
          columnOrder: [ServiceOrderStatusEnum.PRODUCED, ServiceOrderStatusEnum.BILLED],
      };

      return result;
    }

    const handleShowOrder = async (orderId: string) => {
       
      try {
        setLoading(true)
        
        const orderResult = await showOrdersUseCase.handle(orderId);
  
        if (orderResult.isFailure) {
    
          showToast(String(orderResult?.error?.error), 'error');
          return;
        }
  
        const result = orderResult.getValue();
  
        if(result?.id){

          console.log("result handleShowOrder: ", result)
  
          const normalizeSchema : IKanbanBoardFinancialSchemaProps = transformFinancialData(result)
          // const normalizeProductionSchema : IKanbanBoardProductionSchemaProps = transformProductionData(result)
          
          setKanbanFinancialSchema(normalizeSchema)
          // setKanbanProductionSchema(normalizeProductionSchema)
          const normalizeOrder = {
            ...result,
            products: result?.products?.map((i: any) => ({
              ...i,
              productionDueDate: i?.serviceOrder?.dueDate ? format(parseISO(i?.serviceOrder?.dueDate), 'dd/MM/yyyy') : 'Não informado'
            }))
          }
          setOrderData(normalizeOrder)

          if(result?.observations){
            setValue('observations', result?.observations)
          }
        }
  
      } catch (error) {
        console.log("error handleShowOrder function: ", error)
      } finally {
        setLoading(false);
      }
      };

    const handleDetailsOmieOrder = async (orderId?: string) => {
      
      try {
        setLoading(true)
        
        const orderResult = await showOmieOrderDetailsUseCase.handle(String(orderId));
  
        if (orderResult.isFailure) {
    
          showToast(String(orderResult?.error?.error), 'error');
          return;
        }
  
        const result = orderResult.getValue();
  
        if(result?.id){
          setOmieOrderData(result)
        }
  
      } catch (error) {
        console.log("error handleShowOrder function: ", error)
      } finally {
        setLoading(false);
      }
    };
   

    const billProductsOrder = async () => {

      if(!productsToBill?.length){
        showToast(String('Selecione pelo menos 1 produto para faturar'), 'error');
        return;
      }
         
      try {
        setLoading(true)

        const payload : IBillProductOrderPayload = {
          orderId,
          productsIds: productsToBill
        }
        
        const billResult = await billProductOrderOrdersUseCase.handle(payload);

        if (billResult.isFailure) {
     
          showToast(String(billResult?.error?.error), 'error');
          return;
        }

        showToast('Produtos faturados com sucesso', 'success');
        handleShowOrder(orderId)
      } catch (error) {
        console.log("error billProductsOrder function: ", error)
      } finally {
        setLoading(false);
      }
    };

    const handleUpdateBillingFlow = async (billingFlow: TBillingFlow) => {
      try {
        setLoading(true)

        const result = await updateOrdersUseCase.handle({ orderId, billingFlow });

        if (result.isFailure) {
          showToast(String(result?.error?.error), 'error');
          return;
        }

        setOrderData(prev => prev ? { ...prev, billing_flow: billingFlow, billingFlow } : prev)
        showToast('Fluxo de faturamento atualizado com sucesso', 'success');
        handleShowOrder(orderId)
      } catch (error) {
        console.log("error handleUpdateBillingFlow function: ", error)
        showToast('Erro ao atualizar fluxo de faturamento', 'error');
      } finally {
        setLoading(false);
      }
    };

    const handleUpdateOrderWithoutSeller = async () => {
         
      try {
        setLoading(true)

        const result = await updateOrdersUseCase.handle({ sellerId: "0", orderId});

        if (result.isFailure) {
     
          showToast(String(result?.error?.error), 'error');
          return;
        }

        showToast('Pedido atualizado com sucesso', 'success');
        handleShowOrder(orderId)
        setShowModalOrderWithoutSeller(false)
      } catch (error) {
        console.log("error handleUpdateOrderWithoutSeller function: ", error)
      } finally {
        setLoading(false);
      }
    };

    const handleUpdateOrder = async (seller: ISellerEntity) => {
         
      try {
        setLoading(true)

        const result = await updateOrdersUseCase.handle({ sellerId: seller?.id, orderId});

        if (result.isFailure) {
     
          showToast(String(result?.error?.error), 'error');
          return;
        }

        showToast('Pedido atualizado com sucesso', 'success');
        handleShowOrder(orderId)
        setShowCustomersModal(false)
      } catch (error) {
        console.log("error handleUpdateOrderWithoutSeller function: ", error)
      } finally {
        setLoading(false);
      }
    };

   

  const sale = useMemo(() => {
    if(!orderData?.id) return {} as IOrderEntity
    const result : ISaleEntity = {
      ...orderData,
      id: orderData.id,
      createdAt: format(new Date(orderData.createdAt), "dd MMM yyyy", {
        locale: ptBR, 
      }),
      customer: orderData?.customer || {} as ICustomerEntity,
      total: FormatMoney(Number(orderData.totalValue)),
      billingAddress: orderData?.billingAddress || {} as IAddressEntity,
      shippingAddress: orderData?.shippingAddress || {} as IAddressEntity,
      // delivery_address: {
      //   address: orderData?.customer?.addresses?.length ? `${orderData?.customer?.addresses[0].name} ${orderData?.customer?.addresses[0].number}, ${orderData?.customer?.addresses[0].locality} - ${orderData?.customer?.addresses[0].city} - ${orderData?.customer?.addresses[0].province} | ${orderData?.customer?.addresses[0].zipcode}` : 'Não informado.'
      // },
      // billing_address: {
      //   address: orderData?.customer?.addresses?.length ? `${orderData?.customer?.addresses[0].name} ${orderData?.customer?.addresses[0].number}, ${orderData?.customer?.addresses[0].locality} - ${orderData?.customer?.addresses[0].city} - ${orderData?.customer?.addresses[0].province} | ${orderData?.customer?.addresses[0].zipcode}` : 'Não informado.'
      // },
      products: orderData?.products?.map(i => {
        return  {
          id: i.hubProductId,
          name: i.product.name,
          quantity: i.totalQuantity || 0,
          variant: 'Não informado',
          sku: 'Não informado',
          price_unit: FormatMoney(Number(i?.unitaryPrice)),
          total:  FormatMoney(Number(i?.finalPrice || 0)),
          image: i.product.images[0]?.link || Environment.IMAGE_PLACEHOLDER
        }
      })
    }

    return result
  }, [orderData])

  const handleSelectProductToBill = (productId?: string) => {
      if(!productId) return
      const exists = productsToBill.find(i => i === productId)

      if(exists) {
        const normalize = productsToBill.filter(i => i !== productId)
        setProductsToBill(normalize)
      } else {
        setProductsToBill(prev => [...prev, productId])
      }
  }

  const handleSelectAllProductsToBill = () => {
    if (productsToBill?.length === orderData?.products?.length) {
      setProductsToBill([])
    } else {
      setProductsToBill(orderData?.products?.map(i => i.id) || [])
    }
  }

  const handleSelectSeller = (seller: ISellerEntity) => {
    handleUpdateOrder(seller)
   
  }

  const handleUpdateObservations: SubmitHandler<IUpdateObservationPayload> = async (values) => {
    
    try {
      setLoading(true)

      const payload = await updateOrdersUseCase.handle({ orderId, observations: values.observations })

      if (payload.isFailure) {
        
        showToast(String(payload?.error?.error), 'error');
        return;
      }

      showToast('Observação atualizada com sucesso', 'success')
      handleShowOrder(orderId)

    } catch (error) {
        console.log("error: ", error)
        showToast(error.response?.data?.error ?? 'Erro. Por favor, tente novamente', 'error');
      // toast({
      //   position: 'top-right',
      //   render: () => <ToastCustom type='fail' title='Erro' description={error.response?.data?.error ?? 'Erro ao logar. Por favor, tente novamente'} />,
      // });
    } finally {
      setLoading(false);
    }
  };


    return {
        loading,
        billProductsOrder,
        handleShowOrder,
        orderData,
        kanbanFinancialSchema,
        kanbanProductionSchema,
        sale,
        handleSelectProductToBill,
        productsToBill,
        handleDetailsOmieOrder,
        omieOrderData,
        setOmieOrderData,
        handleSelectAllProductsToBill,
      
        showModalOrderWithoutSeller,
        setShowModalOrderWithoutSeller,
        handleUpdateOrderWithoutSeller,
        sellerSelected,
        showCustomersModal,
        handleSelectSeller,
        setShowCustomersModal,
        navigate,
        control,
        handleUpdateObservations,
        handleSubmit,
        register,
        handleUpdateBillingFlow
        
        
    }
}
